// ─── ProfitLens API Service ───────────────────────────────────────────────────
// All communication with the PHP backend goes through this module.

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost/profitlens/backend/api';
const TOKEN_KEY = 'pl_auth_token';

// ─── Token helpers ────────────────────────────────────────────────────────────
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

// ─── Core fetch wrapper ───────────────────────────────────────────────────────
async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${BASE_URL}/${path}`, {
    ...options,
    headers,
  });

  // Try to parse JSON even on error responses
  let body;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const msg = body?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }

  return body;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const auth = {
  async register(name, email, password) {
    const data = await request('auth/register.php', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    });
    setToken(data.token);
    return data;
  },

  async login(email, password) {
    const data = await request('auth/login.php', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setToken(data.token);
    return data;
  },

  logout() {
    setToken(null);
  },

  async me() {
    return request('auth/me.php');
  },
};

// ─── Data (bulk load) ─────────────────────────────────────────────────────────
export const data = {
  async getAll() {
    return request('data.php');
  },
};

// ─── Opening Balances ─────────────────────────────────────────────────────────
export const openingBalances = {
  async getAll() {
    return request('opening_balances.php');
  },

  async upsert(date, amount, note = '') {
    return request('opening_balances.php', {
      method: 'POST',
      body: JSON.stringify({ date, amount, note }),
    });
  },

  async remove(date) {
    return request(`opening_balances.php?date=${encodeURIComponent(date)}`, {
      method: 'DELETE',
    });
  },
};

// ─── Purchases ────────────────────────────────────────────────────────────────
export const purchases = {
  async getAll() {
    return request('purchases.php');
  },

  async create(entry) {
    return request('purchases.php', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  },

  async markPaid(id, paidDate) {
    return request('purchases.php', {
      method: 'PUT',
      body: JSON.stringify({ id, paid: true, paidDate }),
    });
  },

  async remove(id) {
    return request(`purchases.php?id=${id}`, { method: 'DELETE' });
  },
};

// ─── Expenses ─────────────────────────────────────────────────────────────────
export const expenses = {
  async getAll() {
    return request('expenses.php');
  },

  async create(entry) {
    return request('expenses.php', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  },

  async remove(id) {
    return request(`expenses.php?id=${id}`, { method: 'DELETE' });
  },
};

// ─── Sales ────────────────────────────────────────────────────────────────────
export const sales = {
  async getAll() {
    return request('sales.php');
  },

  async create(entry) {
    return request('sales.php', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  },

  async remove(id) {
    return request(`sales.php?id=${id}`, { method: 'DELETE' });
  },
};

// ─── Salaries ────────────────────────────────────────────────────────────────
export const salaries = {
  async getAll() {
    return request('salaries.php');
  },

  async create(entry) {
    return request('salaries.php', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  },

  async remove(id) {
    return request(`salaries.php?id=${id}`, { method: 'DELETE' });
  },
};

// ─── Debts ───────────────────────────────────────────────────────────────────
export const debts = {
  async getAll() {
    return request('debts.php');
  },

  async create(entry) {
    return request('debts.php', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  },

  async recordPayment(id, amount) {
    return request('debts.php', {
      method: 'PUT',
      body: JSON.stringify({ id, action: 'payment', amount }),
    });
  },

  async clear(id) {
    return request('debts.php', {
      method: 'PUT',
      body: JSON.stringify({ id, action: 'clear' }),
    });
  },

  async remove(id) {
    return request(`debts.php?id=${id}`, { method: 'DELETE' });
  },
};
